import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { LDFlagSet } from "launchdarkly-react-client-sdk";
import {
  AccountType,
  ActionList,
  DropdownInput,
  IIconTextLinkProps,
  IOption,
  PageHeader,
  Search,
  Head,
  PerformerAppSmartBanner,
  PerformerAppPath,
  RouterProps,
} from "component-library";
import scrollManager from "shared/services/scrollManager";
import { MapDispatchToProps } from "shared/types/utilities";

import { MY_FILTERS_FULL_PATH, PageTitleKey } from "config/common";
import PageKey from "config/PageKey";

import { IApplicationState } from "store";
import { hidePopup, setLoading, showPopup } from "store/ui/ui.actions";
import { HidePopup, SetLoading, ShowPopup } from "store/ui/ui.types";

import { IJobFilterParams } from "models/api";
import { IActionList, IActionListItem } from "models/common";
import { IJobItem, IPaginatedJobItemList } from "models/job";

import copyPrefixedObjectKeys from "helpers/copyPrefixedObjectKeys";
import { getPageTitle } from "helpers/getPageTitle";
import jobService, { SetJobActionType } from "services/api/job/jobService";

import { GTMEvents } from "app-shell/GoogleTagManager/GTMEvents";

import withJobsFilter, {
  IWithJobsFilterOptions,
  IWithJobsFilterProps,
} from "hoc/withJobsFilter/withJobsFilter";
import {
  FilterType,
  IJobSender,
  OpenClosedRoles,
  ShowType,
  SortingType,
} from "hoc/withJobsFilter/withJobsFilter.config";

import { JobsPageTab, SEARCH_MAX_LENGTH } from "pages/JobsPage/JobsPage.config";
import { JobItemToJobTableItemConverter } from "pages/JobsPage/shared/shared.converters";
import { prepareJobItemToJobTableItemConverterTexts } from "pages/JobsPage/shared/shared.helpers";

import JobsTable, { IJobsTableTexts } from "components/JobsTable/JobsTable";
import { prepareTableTexts } from "components/JobsTable/JobsTable.helpers";
import {
  IJobTableItem,
  IJobTableItemTag,
  JobTableItemUuid,
} from "components/JobsTable/JobsTable.models";

import { IJobsPageInjectedProps } from "pages/JobsPage/JobsPage";

import {
  ActionListItemKey,
  prepareActionList,
} from "pages/JobsPage/MatchingMyFilterPage/MatchingMyFilterPage.helpers";
import {
  LaunchDarklyClientWithFlags,
  withLaunchDarklyConsumerHOC,
} from "shared-services";
import {
  getOpenClosedRolesFilter,
  INPUT_NAMES,
} from "pages/JobsPage/JobsPage.helpers";
import { setJobsFeedData } from "store/jobsFeed/jobsFeed.actions";
import { SetJobsFeedData, IJobsFeedState } from "store/jobsFeed/jobsFeed.types";
import {
  DEFAULT_JOBS_FEEED,
  MATCHING_MY_FILTER_SEARCH,
  initSearch,
  searchRefClear,
  persistSearchAndResetJobsFeedStorage,
  getInitialValue,
} from "shared/helpers/common";

export interface IMatchingMyFilterPageTexts {
  table: IJobsTableTexts;
  pageTitle: string;
}

export interface IMatchingMyFilterPageStoreProps {
  actionList: IActionList;
  texts: IMatchingMyFilterPageTexts;
  convertToTableItem: (
    jobItem: IJobItem,
    ldFlags?: LDFlagSet,
    userType?: AccountType
  ) => IJobTableItem;
  findJobs(
    params: IJobFilterParams,
    abortController?: AbortController
  ): Promise<IPaginatedJobItemList>;
  markAsRead: SetJobActionType;
  markAsUnread: SetJobActionType;
  isPerformer?: boolean;
  jobsFeed: IJobsFeedState;
}

export interface IMatchingMyFilterPageActions {
  setLoading: SetLoading;
  showPopup: ShowPopup;
  hidePopup: HidePopup;
  setJobsFeedData: SetJobsFeedData;
}

export interface IMatchingMyFilterPageProps
  extends
    IMatchingMyFilterPageStoreProps,
    IJobsPageInjectedProps,
    RouterProps,
    IMatchingMyFilterPageActions,
    IWithJobsFilterProps,
    LaunchDarklyClientWithFlags {}

export interface IMatchingMyFilterPageState {
  selectedItems: JobTableItemUuid[];
}

export class MatchingMyFilterPage extends React.Component<
  IMatchingMyFilterPageProps,
  IMatchingMyFilterPageState
> {
  public static pageSize: number = 50;
  public readonly state: Readonly<IMatchingMyFilterPageState> = {
    selectedItems: [],
  };

  private readonly searchRef = React.createRef<Search>();

  private readonly actionListHandlers: {
    [ActionListItemKey.ChangeSettings](
      e: React.SyntheticEvent<HTMLAnchorElement>
    ): void;
  };

  constructor(props: IMatchingMyFilterPageProps) {
    super(props);

    this.setJobsFeedStorage(
      this.props.router.location.pathname,
      DEFAULT_JOBS_FEEED.PAGENUMBER,
      DEFAULT_JOBS_FEEED.BREAKDOWN_ID,
      this.props.jobsFeed.isBackToJobsFeed
    );

    const pageNumber = this.props.jobsFeed.pageNumber;

    if (this.props.jobsFeed.isBackToJobsFeed && pageNumber) {
      const [, setSearchParams] = this.props.router.searchParams;

      setSearchParams({ page: pageNumber });
    }

    const actionListHandlers = {
      [ActionListItemKey.ChangeSettings]: this.handleChangeSettingsClick,
    };
    this.actionListHandlers = copyPrefixedObjectKeys(
      actionListHandlers,
      "more-dropdown_"
    );
  }

  public componentDidMount() {
    this.initSearch();
  }

  public render() {
    const {
      filterValues,
      paginatedItems,
      isLoading,
      commonTexts,
      actionList,
      texts,
      renderJobsFilterControl,
      isPerformer,
    } = this.props;
    const { selectedItems } = this.state;

    return (
      <section>
        <Head registerPageView={this.registerPageView}>
          <title>{texts.pageTitle}</title>
        </Head>
        <PerformerAppSmartBanner appPath={PerformerAppPath.MyJobs} />
        <PageHeader
          pageTitle={commonTexts.title}
          bottomSection={this.renderBottomSection()}
          actionSection={
            <ActionList
              variant="pageHeader"
              items={this.prepareActionListItems(actionList.items)}
              moreButtonText={actionList.moreButtonText}
            />
          }
        />
        {renderJobsFilterControl()}
        <div className="g-content-area">
          <div className="g-col-lg-12 g-table-wrapper">
            <JobsTable
              texts={texts.table}
              items={paginatedItems.items}
              selectedItems={selectedItems}
              deselectItems={this.deselectItems}
              selectItems={this.selectItems}
              onSenderClick={this.handleSenderClick}
              onTagClick={this.handleTagClick}
              itemsPerPage={MatchingMyFilterPage.pageSize}
              isPageLoading={isLoading}
              pageNumber={filterValues[FilterType.PageNumber]}
              itemsCount={paginatedItems.count}
              updatePageNumber={this.handleUpdatePageNumber}
              onMarkAsRead={this.handleMarkAsRead}
              onMarkAsUnread={this.handleMarkAsUnread}
              onRefresh={this.handleRefresh}
              returnUri={MY_FILTERS_FULL_PATH}
              isPerformer={isPerformer}
              jobsFeed={this.props.jobsFeed}
              setJobsFeedData={this.props.setJobsFeedData}
            />
          </div>
        </div>
      </section>
    );
  }

  private renderBottomSection = () => {
    const { commonTexts, filterValues } = this.props;

    return (
      <div className="g-inner-grid-12 g-row-gap-sm g-column-gap-sm">
        <div className="g-col-lg-6 g-col-md-3">
          <Search
            ref={this.searchRef}
            placeholder={commonTexts.searchPlaceholder}
            maxLength={SEARCH_MAX_LENGTH}
            onSearch={this.handleSearch}
            onClear={this.handleSearchClear}
            initialValue={getInitialValue(filterValues)}
          />
        </div>

        {this.renderFilter()}
        {this.renderSorter()}
        {this.renderOpenClosedRoles()}
      </div>
    );
  };

  private renderFilter() {
    const { commonTexts, filterValues } = this.props;

    return (
      <div className="g-col-lg-2 g-col-md-3 g-col-sm-6">
        <DropdownInput<string>
          id="filtersShowType"
          name={INPUT_NAMES.showType}
          variant="select"
          options={commonTexts.filterOptions}
          selected={commonTexts.filterOptions.find(
            (option) => option.value === filterValues[FilterType.ShowType]
          )}
          value={filterValues[FilterType.ShowType]}
          optionSelected={this.handleShowTypeChange}
          notTruncateOptionsOnSmall={true}
        />
      </div>
    );
  }

  private renderSorter() {
    const { commonTexts, filterValues } = this.props;

    return (
      <div className="g-col-lg-2 g-col-md-3 g-col-sm-6">
        <DropdownInput<string>
          id="filtersSortingType"
          name={INPUT_NAMES.sortingType}
          variant="select"
          options={commonTexts.sortingOptions}
          selected={commonTexts.sortingOptions.find(
            (option) => option.value === filterValues[FilterType.SortingType]
          )}
          value={filterValues[FilterType.SortingType]}
          optionSelected={this.handleSortingTypeChange}
          notTruncateOptionsOnSmall={true}
        />
      </div>
    );
  }

  private renderOpenClosedRoles() {
    return (
      <div className="g-col-lg-2 g-col-md-3 g-col-sm-12">
        {getOpenClosedRolesFilter(
          this.props,
          (option: IOption<OpenClosedRoles>) =>
            this.props.changeFilters({
              [FilterType.OpenClosedRoles]: option.value,
            })
        )}
      </div>
    );
  }

  private handleChangeSettingsClick = (
    e: React.SyntheticEvent<HTMLAnchorElement>
  ) => {
    window.location.assign(e.currentTarget.href);
  };

  private readonly setJobsFeedStorage = (
    pathname: string,
    pageNumber: string,
    breakdownId: string,
    isBackToJobsFeed: boolean
  ) => {
    this.props.setJobsFeedData({
      pathname,
      pageNumber,
      breakdownId,
      isBackToJobsFeed,
    });
  };

  private handleUpdatePageNumber = (page: number) => {
    const { router } = this.props;
    const [, setSearchParams] = router.searchParams;

    this.setJobsFeedStorage(
      this.props.jobsFeed.pathname,
      String(page),
      DEFAULT_JOBS_FEEED.BREAKDOWN_ID,
      DEFAULT_JOBS_FEEED.IS_BACK_TO_JOBS_FEED
    );

    setSearchParams({ page: String(page) });
  };

  private handleShowTypeChange = (option: IOption<ShowType>) => {
    this.props.changeFilters({ [FilterType.ShowType]: option.value });
  };

  private handleSortingTypeChange = (option: IOption<SortingType>) => {
    this.props.changeFilters({ [FilterType.SortingType]: option.value });
  };

  private handleSenderClick = (sender: IJobSender) => {
    scrollManager.reset();
    this.props.changeFilters({ [FilterType.Sender]: sender });
  };

  private handleTagClick = (tag: IJobTableItemTag) => {
    scrollManager.reset();
    this.props.addFilterValue(tag.filterType, tag.facetId);
  };

  private handleSearch = (search: string | undefined) => {
    this.deselectAllItems();
    persistSearchAndResetJobsFeedStorage({
      setJobsFeedStorage: this.setJobsFeedStorage,
      pathName: this.props.router.location.pathname,
      tab: MATCHING_MY_FILTER_SEARCH,
      search,
    });

    this.props.changeFilters({ [FilterType.TextFilter]: search });
  };

  private handleSearchClear = () => {
    searchRefClear(this.searchRef);
    this.handleSearch(undefined);
  };

  private createHandlerActionListItemClick =
    (id: string = "") =>
    (e: React.MouseEvent) => {
      e.preventDefault();

      this.actionListHandlers[id]?.(e);
    };

  private prepareActionListItems(
    items: IActionListItem[]
  ): IIconTextLinkProps[] {
    return items?.map(
      (item: IActionListItem): IIconTextLinkProps => ({
        ...item,
        handleClick: this.createHandlerActionListItemClick(item.id),
      })
    );
  }

  private selectItems = (itemIds: JobTableItemUuid[]) => {
    this.setState({ selectedItems: [...this.state.selectedItems, ...itemIds] });
  };

  private deselectItems = (itemIds: JobTableItemUuid[]) => {
    this.setState({
      selectedItems: this.state.selectedItems.filter(
        (item) => !itemIds.includes(item)
      ),
    });
  };

  private deselectAllItems = () => {
    this.setState({ selectedItems: [] });
  };

  private handleMarkAsRead = async (itemIds: JobTableItemUuid[]) => {
    await this.props.onMarkAsRead(itemIds);
    this.deselectAllItems();
  };

  private initSearch() {
    initSearch(
      MATCHING_MY_FILTER_SEARCH,
      this.props.jobsFeed.pageNumber,
      this.props.changeFilters
    );
  }

  private handleMarkAsUnread = async (itemIds: JobTableItemUuid[]) => {
    await this.props.onMarkAsUnread(itemIds);
    this.deselectAllItems();
  };

  private handleRefresh = () => {
    this.props.changeFilters({ [FilterType.PageNumber]: 1 });
    this.props.refreshCounters();
  };

  private registerPageView = () => {
    GTMEvents.jobsFeed.pageViews.matchingMyFilters(this.props.texts.pageTitle);
  };
}

const mapStateToProps = (
  state: IApplicationState,
  ownProps: IJobsPageInjectedProps
): IMatchingMyFilterPageStoreProps => {
  const pageTranslations = state.translation.pages[PageKey.Jobs]!;
  const commonTranslations = state.translation.common;
  const userType = state.user.data!.type;

  const convertToTableItem = new JobItemToJobTableItemConverter(
    prepareJobItemToJobTableItemConverterTexts(
      pageTranslations,
      commonTranslations
    ),
    ownProps.productionTypes,
    ownProps.contractTypes
  ).convert;

  return {
    actionList: prepareActionList(pageTranslations),
    texts: {
      table: prepareTableTexts(commonTranslations),
      pageTitle: getPageTitle(
        commonTranslations,
        PageTitleKey.JobsFeedMatchingMySettings
      ),
    },
    findJobs: (...args) => jobService.find(...args),
    markAsRead: (...args) => jobService.markAsRead(...args),
    markAsUnread: (...args) => jobService.markAsUnread(...args),
    convertToTableItem,
    isPerformer: userType === AccountType.Performer,
    jobsFeed: state.jobsFeed,
  };
};

const mapDispatchToProps: MapDispatchToProps<IMatchingMyFilterPageActions> = {
  setLoading,
  showPopup,
  hidePopup,
  setJobsFeedData,
};

export const withJobsFilterOptions: IWithJobsFilterOptions = {
  tab: JobsPageTab.MyFilter,
  applyUserSettings: true,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLaunchDarklyConsumerHOC(),
  withJobsFilter<IMatchingMyFilterPageProps>(withJobsFilterOptions)
)(MatchingMyFilterPage);
